import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, FileText, Table as TableIcon, Play, ChevronRight } from 'lucide-react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../styles/calendar-custom.css';
import { postsAPI } from '../../services/api';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl, getMediaTypeFromUrl } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { PLATFORMS } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';

const localizer = momentLocalizer(moment);

const ScheduledPostsPage = () => {
  const { isRTL, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'table' or 'calendar'

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all posts (backend already filters by role/agency)
      const response = await postsAPI.getAll({});
      const allPosts = response.data.posts || [];
      // Consider a post "scheduled" if it has a scheduled_at value
      const scheduledPosts = allPosts.filter((post) => post.scheduled_at);
      setPosts(scheduledPosts);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <Loading fullScreen />;

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} variant="secondary">
          {isRTL ? 'إعادة المحاولة' : 'Try Again'}
        </Button>
      </Card>
    );
  }

  // Sort posts by scheduled_at (or created_at as fallback)
  const sortedPosts = [...posts].sort((a, b) => {
    const aDate = a.scheduled_at ? new Date(a.scheduled_at) : new Date(a.created_at);
    const bDate = b.scheduled_at ? new Date(b.scheduled_at) : new Date(b.created_at);
    return aDate - bDate;
  });

  const totalScheduled = sortedPosts.length;

  // Calendar events
  const calendarEvents = sortedPosts.map((post) => ({
    id: post.id,
    title: post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post'),
    start: new Date(post.scheduled_at),
    end: new Date(post.scheduled_at),
    resource: post,
  }));

  // Custom calendar event component
  const CalendarEventComponent = ({ event }) => {
    const post = event.resource;
    const timeStr = event.start ? new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    const platformInfo = post?.platform ? PLATFORMS[post.platform] : null;
    const hasImage = post?.current_image_url;

    return (
      <div className={`flex items-center gap-2 overflow-hidden h-full min-h-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {hasImage && (
          <div className="w-8 h-8 rounded flex-shrink-0 overflow-hidden bg-white/20 ring-1 ring-white/30">
            <img
              src={getImageUrl(post.current_image_url)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        {platformInfo && !hasImage && (
          <span className="flex-shrink-0 text-base leading-none">{platformInfo.icon}</span>
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <span className="block truncate font-semibold text-sm">{event.title}</span>
          <div className={`flex items-center gap-1.5 text-xs opacity-95 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {timeStr && (
              <span className="inline-flex items-center gap-0.5 shrink-0">
                <Clock className="w-3 h-3" />
                {timeStr}
              </span>
            )}
            {post?.client_name && (
              <>
                <span className="shrink-0">·</span>
                <span className="truncate">{post.client_name}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Event styling based on status
  const eventStyleGetter = (event) => {
    const post = event.resource;
    let backgroundColor = '#0ea5e9'; // sky-500
    
    if (post?.status === 'approved') {
      backgroundColor = '#10b981'; // green-500
    } else if (post?.status === 'scheduled') {
      backgroundColor = '#3b82f6'; // blue-500
    } else if (post?.status === 'needs_changes') {
      backgroundColor = '#f59e0b'; // amber-500
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '8px',
        opacity: 0.95,
        color: 'white',
        border: 'none',
        padding: '6px 10px',
        fontSize: '0.8125rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      },
    };
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-2 mb-1">
              <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-sky-100 text-sky-700">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'إدارة المنشورات المجدولة' : 'Scheduled Posts'}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {isRTL ? 'المنشورات المجدولة' : 'Scheduled Posts'}
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              {isRTL
                ? 'عرض وإدارة جميع المنشورات المجدولة للنشر في مكان واحد.'
                : 'View and manage all posts scheduled for publishing in one place.'}
            </p>
          </div>

          {/* Quick stats pill */}
          <div className="flex flex-col items-start md:items-end gap-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-slate-200 px-3 py-1.5 shadow-sm">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-500 text-white text-xs font-semibold">
                {totalScheduled}
              </span>
              <span className="text-xs sm:text-sm font-medium text-slate-700">
                {isRTL
                  ? totalScheduled === 1
                    ? 'منشور مجدول'
                    : 'منشورات مجدولة'
                  : totalScheduled === 1
                  ? 'Scheduled Post'
                  : 'Scheduled Posts'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        <Button
          onClick={() => setViewMode('table')}
          variant={viewMode === 'table' ? 'primary' : 'outline'}
          size="sm"
          className={viewMode === 'table' ? 'bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30' : ''}
        >
          <TableIcon className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {isRTL ? 'عرض جدول' : 'Table View'}
        </Button>
        <Button
          onClick={() => setViewMode('calendar')}
          variant={viewMode === 'calendar' ? 'primary' : 'outline'}
          size="sm"
          className={viewMode === 'calendar' ? 'bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30' : ''}
        >
          <Calendar className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {isRTL ? 'عرض تقويم' : 'Calendar View'}
        </Button>
      </div>

      {/* Content */}
      {viewMode === 'calendar' ? (
        <Card className="p-4 border-slate-200 bg-white/80 shadow-sm">
          {sortedPosts.length === 0 ? (
            <div className={`py-10 flex flex-col items-center ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="mb-3 rounded-full bg-slate-100 w-12 h-12 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-600 text-sm font-medium mb-1">
                {isRTL ? 'لا توجد منشورات مجدولة حالياً' : 'No scheduled posts yet'}
              </p>
              <p className="text-slate-400 text-xs max-w-md text-center">
                {isRTL
                  ? 'بعد تعميد البوست يمكنك جدولة وقت النشر من صفحة تفاصيل البوست ليظهر هنا.'
                  : 'After approving a post you can schedule its publish time from the post details page and it will appear here.'}
              </p>
            </div>
          ) : (
            <div style={{ height: 600 }}>
              <BigCalendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={(event) => navigate(`/posts/${event.id}`)}
                eventPropGetter={eventStyleGetter}
                components={{ event: CalendarEventComponent }}
                rtl={isRTL}
                messages={{
                  next: isRTL ? 'التالي' : 'Next',
                  previous: isRTL ? 'السابق' : 'Previous',
                  today: isRTL ? 'اليوم' : 'Today',
                  month: isRTL ? 'شهر' : 'Month',
                  week: isRTL ? 'أسبوع' : 'Week',
                  day: isRTL ? 'يوم' : 'Day',
                  agenda: isRTL ? 'جدول الأعمال' : 'Agenda',
                  date: isRTL ? 'التاريخ' : 'Date',
                  time: isRTL ? 'الوقت' : 'Time',
                  event: isRTL ? 'حدث' : 'Event',
                  noEventsInRange: isRTL ? 'لا توجد أحداث في هذا النطاق.' : 'There are no events in this range.',
                  showMore: (total) => isRTL ? `+${total} المزيد` : `+${total} more`,
                }}
              />
            </div>
          )}
        </Card>
      ) : (
        <Card className="p-4 border-slate-200 bg-white/80 shadow-sm">
        {sortedPosts.length === 0 ? (
          <div className={`py-10 flex flex-col items-center ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="mb-3 rounded-full bg-slate-100 w-12 h-12 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-slate-600 text-sm font-medium mb-1">
              {isRTL ? 'لا توجد منشورات مجدولة حالياً' : 'No scheduled posts yet'}
            </p>
            <p className="text-slate-400 text-xs max-w-md text-center">
              {isRTL
                ? 'بعد تعميد البوست يمكنك جدولة وقت النشر من صفحة تفاصيل البوست ليظهر هنا.'
                : 'After approving a post you can schedule its publish time from the post details page and it will appear here.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sortedPosts.map((post) => {
              const dt = post.scheduled_at ? new Date(post.scheduled_at) : new Date(post.created_at);
              const dateStr = formatDate(dt.toISOString());
              const timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
              const isVideo = post.current_image_url && getMediaTypeFromUrl(post.current_image_url) === 'video';

              return (
                <Link
                  key={post.id}
                  to={`/posts/${post.id}`}
                  className="block group"
                >
                  <Card className="overflow-hidden h-full flex flex-col hover:shadow-lg hover:border-sky-200/60 transition-all duration-200">
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-slate-100 overflow-hidden">
                      {post.current_image_url ? (
                        isVideo ? (
                          <>
                            <video
                              src={getImageUrl(post.current_image_url)}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                              onError={(e) => {
                                const el = e.target.parentElement;
                                if (el) {
                                  el.innerHTML = '<div class="w-full h-full flex items-center justify-center"><p class="text-slate-400 text-sm">No video</p></div>';
                                  el.className = 'aspect-video bg-slate-100 flex items-center justify-center';
                                }
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                <Play className="w-6 h-6 text-slate-800 ms-1" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={getImageUrl(post.current_image_url)}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.className = 'aspect-video bg-slate-100 flex items-center justify-center';
                              e.target.parentElement.innerHTML = '<p class="text-slate-400 text-sm">No image</p>';
                            }}
                          />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      {/* Platform badge */}
                      {platformInfo && (
                        <div className={`absolute top-2 ${isRTL ? 'left-2' : 'right-2'} w-8 h-8 rounded-lg bg-black/50 backdrop-blur flex items-center justify-center`}>
                          <PlatformIcon platformId={platformInfo.id} className="w-5 h-5 text-white" />
                        </div>
                      )}
                      {/* Schedule badge - prominent */}
                      <div className={`absolute bottom-2 ${isRTL ? 'right-2 left-2' : 'left-2 right-2'} flex items-center justify-between gap-2`}>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-500/95 text-white text-xs font-semibold shadow-lg">
                          <Clock className="w-3.5 h-3.5" />
                          {dateStr} · {timeStr}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className={`font-bold text-slate-900 line-clamp-1 flex-1 group-hover:text-sky-700 transition-colors text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
                        </h3>
                        <StatusBadge status={post.status} size="sm" isRTL={isRTL} />
                      </div>
                      {post.client_name && (
                        <p className={`text-xs text-slate-500 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {post.client_name}
                        </p>
                      )}
                      {post.description && (
                        <p className={`text-xs text-slate-600 line-clamp-2 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {post.description}
                        </p>
                      )}
                      <div className="mt-auto pt-2 border-t border-slate-100">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium text-sky-600 group-hover:text-sky-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {isRTL ? 'عرض التفاصيل' : 'View details'}
                          <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
        </Card>
      )}
    </div>
  );
};

export default ScheduledPostsPage;

